import {
  DifficultyLevelEnum,
  Question,
  Question_QuestionEnum,
} from '@protos/content_management/content.db_pb';
import {
  MarkingSchemeEnum,
  TaskInfo,
} from '@protos/learning_management/lms.db_pb';
import { HomeworkTask } from '@protos/learning_management/lms.hw.common.apis_pb';
import { IRequiredQuestionCountInfoForDifficultyLevel } from '../../reducer/assessmentFlow.slice';
import { cloneObject } from '@geneo2-web/shared-ui';
import { AssessmentTask } from '@protos/learning_management/lms.assessment.common.apis_pb';
import {
  DIAGNOSTIC,
  FORMATIVE,
} from '../../../../routeHandling/RoutesNomenclature';
import {
  ModuleFilteredQuestions,
  TaskInfoModel,
} from '@protos/learning_management/lms.assessment.teacher.apis_pb';

export function updateApiResponse(
  apiResponse: ModuleFilteredQuestions,
  previousApiResponse: ModuleFilteredQuestions,
  newQuestionsCount: IRequiredQuestionCountInfoForDifficultyLevel
): ModuleFilteredQuestions {
  const previousQuestionsCount = {
    noOfLowQuestions: 0,
    noOfMediumQuestions: 0,
    noOfHighQuestions: 0,
  };
  // Calculate the previous count of each difficulty level in the existing response
  for (const question of previousApiResponse.questions) {
    const difficultyLevel =
      question.questionMeta?.difficultyLevel ||
      DifficultyLevelEnum.DIFFICULTY_LEVEL_UNDEFINED;
    incrementDifficultyLevelCount(previousQuestionsCount, difficultyLevel);
  }
  const updatedQuestions: Question[] = [];

  // Helper function to add questions to the updatedQuestions array based on their difficulty level and available count
  const addSubractQuestions = (
    difficulty: DifficultyLevelEnum,
    oldDifficultyCount: number,
    newDifficultyCount: number
  ) => {
    const existingQuestions = previousApiResponse.questions.filter(
      (q) => q.questionMeta?.difficultyLevel === difficulty
    );
    if (oldDifficultyCount > newDifficultyCount) {
      updatedQuestions.push(...existingQuestions.slice(0, newDifficultyCount));
    } else if (oldDifficultyCount < newDifficultyCount) {
      const newQuestions = apiResponse.questions
        .filter((q) => q.questionMeta?.difficultyLevel === difficulty)
        .filter(
          (q) =>
            !existingQuestions.some(
              (existingQ) => existingQ.questionId === q.questionId
            )
        );
      updatedQuestions.push(
        ...existingQuestions,
        ...newQuestions.slice(0, newDifficultyCount - oldDifficultyCount)
      );
    } else {
      updatedQuestions.push(...existingQuestions);
    }

    // console.log(
    //   'module_addSubractQuestions',
    //   difficulty,
    //   existingQuestions,
    //   oldDifficultyCount,
    //   newDifficultyCount,
    //   updatedQuestions
    // );
  };
  addSubractQuestions(
    DifficultyLevelEnum.DIFFICULTY_LEVEL_LOW,
    previousQuestionsCount.noOfLowQuestions,
    newQuestionsCount.noOfLowQuestions
  );
  addSubractQuestions(
    DifficultyLevelEnum.DIFFICULTY_LEVEL_MEDIUM,

    previousQuestionsCount.noOfMediumQuestions,
    newQuestionsCount.noOfMediumQuestions
  );
  addSubractQuestions(
    DifficultyLevelEnum.DIFFICULTY_LEVEL_HIGH,
    previousQuestionsCount.noOfHighQuestions,
    newQuestionsCount.noOfHighQuestions
  );
  return { questions: updatedQuestions } as ModuleFilteredQuestions;
}

const incrementDifficultyLevelCount = (
  countObject: {
    noOfLowQuestions: number;
    noOfMediumQuestions: number;
    noOfHighQuestions: number;
  },
  difficulty: DifficultyLevelEnum
) => {
  if (difficulty === DifficultyLevelEnum.DIFFICULTY_LEVEL_LOW) {
    countObject.noOfLowQuestions++;
  } else if (difficulty === DifficultyLevelEnum.DIFFICULTY_LEVEL_MEDIUM) {
    countObject.noOfMediumQuestions++;
  } else if (difficulty === DifficultyLevelEnum.DIFFICULTY_LEVEL_HIGH) {
    countObject.noOfHighQuestions++;
  }
};

export const createTaskInfoModel = (
  questions: Question[],
  jeeMarking?: boolean
): TaskInfoModel[] => {
  const arr = questions
    .filter((question) => !!question.questionId)
    .map((question, index) => {
      const common = question.question?.model.value?.commonQuestionContent;
      let derivedMaxMarks: number | undefined;
      if (jeeMarking) {
        // JEE marking:
        derivedMaxMarks = 4;
      } else if (common?.marks && common.marks.length > 0) {
        derivedMaxMarks = common.marks.reduce((sum, mark) => sum + mark, 0);
      } else {
        derivedMaxMarks = undefined; // or 1 as a default if needed
      }
      return new TaskInfoModel({
        questionId: question.questionId
          ? question.questionId
          : index.toString(),
        sequence: index + 1,
        maxMarks: derivedMaxMarks,
      });
    });
  return arr;
};

export const createTaskInfoModel_2 = (
  questions: Question[],
  earlierQuestions?: TaskInfoModel[]
): TaskInfoModel[] => {
  return questions
    .filter((q) => !!q.questionId)
    .map((question, index) => {
      const previousTaskInfo = earlierQuestions?.find(
        (t) => t.questionId === question.questionId
      );
      // Preserve previous maxMarks if exists, otherwise calculate from current question
      let derivedMaxMarks: number | undefined = previousTaskInfo?.maxMarks;

      if (derivedMaxMarks === undefined) {
        const commonMarks =
          question.question?.model.value?.commonQuestionContent?.marks;
        if (commonMarks && commonMarks.length > 0) {
          derivedMaxMarks = commonMarks.reduce((sum, mark) => sum + mark, 0);
        }
      }
      return new TaskInfoModel({
        questionId: question.questionId,
        sequence: index + 1,
        maxMarks: derivedMaxMarks,
      });
    });
};

export const rearrangeTaskInfoArrayAfterDeletion = (
  deletedQuestionId: string,
  task_info_array_input?: TaskInfoModel[]
) => {
  const task_info_array = cloneObject(task_info_array_input);
  if (!task_info_array) {
    return;
  }
  const indexToDelete = task_info_array.findIndex(
    (task) => task.questionId === deletedQuestionId
  );
  if (indexToDelete !== -1) {
    task_info_array.splice(indexToDelete, 1);
  }
  // Sort the array based on the 'sequence' property in ascending order
  task_info_array.sort((a, b) => a.sequence - b.sequence);
  // Update the sequence numbers to ensure they are consecutive
  task_info_array.forEach((task, index) => {
    task.sequence = index + 1;
  });
  return task_info_array;
};

export const rearrangeTaskInfoArrayAfterAddition = (
  addedQuestionId: string,
  task_info_array_input?: TaskInfoModel[]
) => {
  const task_info_array = cloneObject(task_info_array_input);
  if (!task_info_array) {
    return;
  }
  const maxSequence = task_info_array.reduce(
    (max, task) => (task.sequence > max ? task.sequence : max),
    0
  );
  task_info_array.push(
    new TaskInfoModel({
      questionId: addedQuestionId,
      sequence: maxSequence + 1,
    })
  );
  task_info_array.sort((a, b) => a.sequence - b.sequence);
  return task_info_array;
};
export const rearrangeTaskInfoArrayAfterMove = (
  newQuestions: Question[],
  task_info_array_input?: TaskInfoModel[]
) => {
  const task_info_array = cloneObject(task_info_array_input);
  if (!task_info_array) {
    return [];
  }
  // Create a mapping of questionId to index in the newQuestions array
  const questionIdToIndexMap: { [questionId: string]: number | undefined } = {};
  for (let i = 0; i < newQuestions.length; i++) {
    const question = newQuestions[i];
    if (question.questionId !== undefined) {
      questionIdToIndexMap[question.questionId] = i;
    }
  }
  // Update the sequence numbers based on the new ordering in newQuestions
  task_info_array.forEach((task) => {
    const newIndex = questionIdToIndexMap[task.questionId];
    if (newIndex !== undefined) {
      task.sequence = newIndex + 1;
    }
  });
  // Sort the array based on the updated 'sequence' property in ascending order
  task_info_array.sort((a, b) => a.sequence - b.sequence);
  return task_info_array;
};

export const moveQuestionId = ({
  selectedTasksInfoInput,
  oldQuestionId,
  newQuestionId,
}: {
  selectedTasksInfoInput: TaskInfoModel[];
  oldQuestionId: string;
  newQuestionId: string;
}): TaskInfoModel[] => {
  const targetSequence = selectedTasksInfoInput.reduce<number | null>(
    (sequence, taskInfo) => {
      if (taskInfo.questionId === oldQuestionId) {
        return taskInfo.sequence;
      }
      return sequence;
    },
    null
  );

  if (targetSequence === null) {
    return selectedTasksInfoInput; // No matching sequence found, return original array
  }
  const arr = selectedTasksInfoInput.map((taskInfo) => {
    if (taskInfo.sequence === targetSequence) {
      return new TaskInfoModel({
        ...taskInfo,
        questionId: newQuestionId,
      });
    }
    return taskInfo;
  });

  return arr;
};
export const createQuestionCountForDifficultyLevel = (
  difficultyLevel: DifficultyLevelEnum,
  count?: IRequiredQuestionCountInfoForDifficultyLevel
): IRequiredQuestionCountInfoForDifficultyLevel => {
  const questions_count: IRequiredQuestionCountInfoForDifficultyLevel = {
    noOfLowQuestions: 0,
    noOfMediumQuestions: 0,
    noOfHighQuestions: 0,
  };
  switch (difficultyLevel) {
    case DifficultyLevelEnum.DIFFICULTY_LEVEL_HIGH:
      questions_count.noOfHighQuestions =
        count && count.noOfHighQuestions > 1 ? count.noOfHighQuestions : 1;
      break;
    case DifficultyLevelEnum.DIFFICULTY_LEVEL_MEDIUM:
      questions_count.noOfMediumQuestions =
        count && count.noOfMediumQuestions > 1 ? count.noOfMediumQuestions : 1;
      break;
    case DifficultyLevelEnum.DIFFICULTY_LEVEL_LOW:
      questions_count.noOfLowQuestions =
        count && count.noOfLowQuestions > 1 ? count.noOfLowQuestions : 1;
      break;
    default:
      // Handle undefined or other cases if necessary
      break;
  }
  return questions_count;
};
export const deduceMaxQuestionsCount = (
  difficultyLevel: DifficultyLevelEnum,
  count?: IRequiredQuestionCountInfoForDifficultyLevel
) => {
  switch (difficultyLevel) {
    case DifficultyLevelEnum.DIFFICULTY_LEVEL_HIGH:
      return count?.noOfHighQuestions;
    case DifficultyLevelEnum.DIFFICULTY_LEVEL_MEDIUM:
      return count?.noOfMediumQuestions;
    case DifficultyLevelEnum.DIFFICULTY_LEVEL_LOW:
      return count?.noOfLowQuestions;
    default:
      return 0;
  }
};
export function convertToSequenceInfo(
  taskInfo: TaskInfo | undefined
): TaskInfoModel[] {
  if (!taskInfo) {
    return [];
  }
  const sequenceInfo = taskInfo.taskQuestionInfo.map((questionInfo) => {
    return new TaskInfoModel({
      questionId: questionInfo.questionId,
      sequence: questionInfo.sequence,
      maxMarks: questionInfo.maxMarks,
    });
  });
  return sequenceInfo;
}

export const calculateQuestionsCount = (
  questionsList: ModuleFilteredQuestions | undefined
): IRequiredQuestionCountInfoForDifficultyLevel => {
  const questions_count = {
    noOfLowQuestions: 0,
    noOfMediumQuestions: 0,
    noOfHighQuestions: 0,
  };
  if (!questionsList) {
    return questions_count;
  }
  const list = questionsList.questions;
  for (let i = 0; i < list.length; i++) {
    switch (list[i].questionMeta?.difficultyLevel) {
      case DifficultyLevelEnum.DIFFICULTY_LEVEL_HIGH:
        questions_count.noOfHighQuestions++;
        break;
      case DifficultyLevelEnum.DIFFICULTY_LEVEL_MEDIUM:
        questions_count.noOfMediumQuestions++;
        break;
      case DifficultyLevelEnum.DIFFICULTY_LEVEL_LOW:
        questions_count.noOfLowQuestions++;
        break;
      default:
        break;
    }
  }
  return questions_count;
};

export const canEdit = (
  teacher_id: string,
  fetchedHomeworkDetails?: AssessmentTask
) => {
  if (fetchedHomeworkDetails) {
    if (fetchedHomeworkDetails.teacherId === BigInt(teacher_id)) {
      return true;
    }
    return false;
  }
  return true;
};

type AssessmentType = 'DIAGNOSTIC' | 'FORMATIVE' | 'HOMEWORK';

export const getAssessmentTypeName = (pathname: string): AssessmentType => {
  if (pathname.includes(DIAGNOSTIC)) {
    return 'DIAGNOSTIC';
  } else if (pathname.includes(FORMATIVE)) {
    return 'FORMATIVE';
  } else {
    return 'HOMEWORK';
  }
};

export const getDifficultyLevelFromString = (
  difficultyLevel: string
): DifficultyLevelEnum => {
  switch (difficultyLevel) {
    case 'Easy':
      return DifficultyLevelEnum.DIFFICULTY_LEVEL_LOW;
    case 'Medium':
      return DifficultyLevelEnum.DIFFICULTY_LEVEL_MEDIUM;
    case 'Hard':
      return DifficultyLevelEnum.DIFFICULTY_LEVEL_HIGH;
    default:
      return DifficultyLevelEnum.DIFFICULTY_LEVEL_UNDEFINED;
  }
};

export const getQuestionTypesArrayFromQuestionsList = (
  questionsList: ModuleFilteredQuestions | undefined
): { title: string; typeKey: Question_QuestionEnum }[] => {
  const questionTypesSet = new Set<Question_QuestionEnum>();
  if (!questionsList) {
    return [];
  }
  questionsList.questions.forEach((question) => {
    if (question.questionType !== undefined) {
      questionTypesSet.add(question.questionType);
    }
  });
  return Array.from(questionTypesSet)
    .map(getQuestionTypeTitleAndEnum)
    .filter(
      (item): item is { title: string; typeKey: Question_QuestionEnum } =>
        !!item
    );
};

export function getQuestionTypeTitleAndEnum(
  typeKey: Question_QuestionEnum
): { title: string; typeKey: Question_QuestionEnum } | null {
  switch (typeKey) {
    case Question_QuestionEnum.QUESTION_TYPE_MCQS:
      return { title: 'MCQ Single', typeKey };
    case Question_QuestionEnum.QUESTION_TYPE_MCQM:
      return { title: 'MCQ Multiple', typeKey };
    case Question_QuestionEnum.QUESTION_TYPE_TF:
      return { title: 'True/False', typeKey };
    case Question_QuestionEnum.QUESTION_TYPE_FIB:
      return { title: 'Fill in the Blanks', typeKey };
    case Question_QuestionEnum.QUESTION_TYPE_VERY_SHORT:
      return { title: 'Very Short', typeKey };
    case Question_QuestionEnum.QUESTION_TYPE_SHORT:
      return { title: 'Short', typeKey };
    case Question_QuestionEnum.QUESTION_TYPE_LONG:
      return { title: 'Long Answer', typeKey };
    case Question_QuestionEnum.QUESTION_TYPE_CBQ:
      return { title: 'Competency Based', typeKey };
    default:
      return null;
  }
}
export function getQuestionTypeTitleAndEnumFromString(
  typeName: string
): { title: string; typeKey: Question_QuestionEnum } | null {
  switch (typeName) {
    case 'MCQ Single':
      return {
        title: typeName,
        typeKey: Question_QuestionEnum.QUESTION_TYPE_MCQS,
      };
    case 'MCQ Multiple':
      return {
        title: typeName,
        typeKey: Question_QuestionEnum.QUESTION_TYPE_MCQM,
      };
    case 'True/False':
      return {
        title: typeName,
        typeKey: Question_QuestionEnum.QUESTION_TYPE_TF,
      };
    case 'Fill in the Blanks':
      return {
        title: typeName,
        typeKey: Question_QuestionEnum.QUESTION_TYPE_FIB,
      };
    case 'Very Short':
      return {
        title: typeName,
        typeKey: Question_QuestionEnum.QUESTION_TYPE_VERY_SHORT,
      };
    case 'Short':
      return {
        title: typeName,
        typeKey: Question_QuestionEnum.QUESTION_TYPE_SHORT,
      };
    case 'Long Answer':
      return {
        title: typeName,
        typeKey: Question_QuestionEnum.QUESTION_TYPE_LONG,
      };
    case 'Competency Based':
      return {
        title: typeName,
        typeKey: Question_QuestionEnum.QUESTION_TYPE_CBQ,
      };
    default:
      return null;
  }
}
export const JEE_MARKING_SCHEME_ALLOWED_TYPES: Question_QuestionEnum[] = [
  Question_QuestionEnum.QUESTION_TYPE_MCQS,
  Question_QuestionEnum.QUESTION_TYPE_FIB,
];

export const getAllQuestionTypeTitleAndEnumArray = (): {
  title: string;
  typeKey: Question_QuestionEnum;
}[] => {
  return Object.values(Question_QuestionEnum)
    .filter((v) => typeof v === 'number') // Only enum values, not keys
    .map((typeKey) =>
      getQuestionTypeTitleAndEnum(typeKey as Question_QuestionEnum)
    )
    .filter(
      (item): item is { title: string; typeKey: Question_QuestionEnum } =>
        !!item
    );
};

export const QuestionTypeConsecutiveEnum = {
  noOfMcqs: Question_QuestionEnum.QUESTION_TYPE_MCQS,
  noOfMcqm: Question_QuestionEnum.QUESTION_TYPE_MCQM,
  noOfFib: Question_QuestionEnum.QUESTION_TYPE_FIB,
  noOfTf: Question_QuestionEnum.QUESTION_TYPE_TF,
  noOfVeryShort: Question_QuestionEnum.QUESTION_TYPE_VERY_SHORT,
  noOfShort: Question_QuestionEnum.QUESTION_TYPE_SHORT,
  noOfLong: Question_QuestionEnum.QUESTION_TYPE_LONG,
  noOfCbq: Question_QuestionEnum.QUESTION_TYPE_CBQ,
};
export const markingSchemeOptions = [
  {
    title: 'Geneo Marking Scheme',
    type: MarkingSchemeEnum.MARKING_SCHEME_GENEO,
  },
  { title: 'JEE Marking Scheme', type: MarkingSchemeEnum.MARKING_SCHEME_JEE },
];
